import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  TablePagination,
  Button
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./Repositories.css";

const API_BASE = process.env.REACT_APP_API_URL;

function Repositories() {
  const navigate = useNavigate();
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Show 10 repositories per page by default

  useEffect(() => {
    const fetchRepositories = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          'https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/github/repositories',
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch repositories");
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error("API response is not an array");
        }
        setRepositories(data);
      } catch (error) {
        console.error("Error fetching repositories:", error);
        setRepositories([]); // Ensure state is always an array
      } finally {
        setLoading(false);
      }
    };
    fetchRepositories();
  }, []);
  

  // ✅ Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleBack = (e) => {
    console.log('Back button clicked');
    try {
      navigate(-1);
    } catch (error) {
      console.error('Navigation error:', error);
      window.history.back(); // Fallback
    }
  };

  return (
    <div className="repositories-container">
      <button className="repositories-back-button" onClick={handleBack}>
        &lt; Back
      </button>
      <div className="repositories-breadcrumb">
        <a href="/home" className="repositories-breadcrumb-link">Dashboard</a>
        <span className="repositories-breadcrumb-separator"> &gt; </span>
        <a href="/monitoring-dashboard" className="repositories-breadcrumb-link">Monitoring</a>
        <span className="repositories-breadcrumb-separator"> &gt; </span>
        <a href="/github-dashboard" className="repositories-breadcrumb-link">GitHub Monitoring</a>
        <span className="repositories-breadcrumb-separator"> &gt; </span>
        <span className="repositories-breadcrumb-current">Repositories</span>
      </div>
      <div className="repositories-header">
        <div className="title-row">
          <span className="repositories-title">Organization Repositories</span>
          <span className="repositories-count"> (Total Repositories: <span className="count-number">{repositories.length}</span>)</span>
        </div>
        <div className="repositories-divider"></div>
        <div className="repositories-description">
          <p><strong>Browse all repositories within your organization, along with their contributors.</strong></p>
          <p>Track code ownership, collaboration, and activity across projects at a glance.</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : (
        <TableContainer component={Paper} className="repositories-table-container">
          <Table className="repositories-table" aria-label="repositories table">
            <TableHead>
              <TableRow>
                <TableCell className="repositories-table-header">S.No</TableCell>
                <TableCell className="repositories-table-header">Repository Name</TableCell>
                <TableCell className="repositories-table-header">Contributors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {repositories
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((repo, index) => (
                  <TableRow 
                    key={repo.name} 
                    className={`repositories-table-row ${index % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <TableCell className="repositories-table-cell">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell className="repositories-table-cell">
                      <a 
                        href={repo.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="repo-link"
                      >
                        {repo.name}
                      </a>
                    </TableCell>
                    <TableCell className="repositories-table-cell">
                      {Array.isArray(repo.contributors) && repo.contributors.length > 0
                        ? repo.contributors.join(", ")
                        : "No Contributors"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <div className="repositories-pagination-row">
            <div className="pagination-controls">
              <button 
                className="pagination-back-button"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
              <div className="pagination-right">
                <div className="rows-per-page">
                  <span className="rows-label">Rows per page:</span>
                  <select
                    className="rows-select"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  >
                    {[10, 20, 50, 100].map((rows) => (
                      <option key={rows} value={rows}>
                        {rows}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="page-info">
                  {`${page * rowsPerPage + 1}-${Math.min(
                    (page + 1) * rowsPerPage,
                    repositories.length
                  )} of ${repositories.length}`}
                </span>
                <button
                  className="pagination-nav"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  &lt;
                </button>
                <button
                  className="pagination-nav"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(
                        Math.ceil(repositories.length / rowsPerPage) - 1,
                        p + 1
                      )
                    )
                  }
                  disabled={
                    page >= Math.ceil(repositories.length / rowsPerPage) - 1
                  }
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </TableContainer>
      )}
    </div>
  );
}

export default Repositories;
